import React from 'react';
import PropTypes from 'prop-types';
import CustomLink from '@bitComponents/unifiedComponents/CustomLink';
import Button, {
  BUTTON_HEIGHT,
  BUTTON_WIDTH,
  BUTTON_COLOR,
  ICON_POSITION,
} from '@bitComponents/unifiedComponents/Button';
import TextInfo, {
  TEXT_HEADING_LEVEL,
  TEXT_KIND,
  TEXT_DISPLAY,
  TEXT_COLOR,
  TEXT_ALIGN,
  TEXT_SIZE,
  TEXT_FONT_WEIGHT,
} from '@gcloud-npm/components.text';
import SvgIconLoader, {
  ICONS_ID,
  COLOR as ICON_COLOR,
  SVG_ICON_KIND,
} from '@bitComponents/unifiedIcons/SvgIconLoader';
import { getDeviceKindDependsOnViewPort } from '@bitComponents/utils/common';
import { DEVICES } from '@bitComponents/utils/constants';
import { CALL_4_SELLING_CONTENT } from '@bitComponents/utils/tagging';
import './Alert.scss';

export default function Alert({
  errorToPrint,
  taggingInfo,
  iconColor,
  iconId,
}) {
  const { title, msg, secondaryMessage, phone, link, buttons } = errorToPrint;
  const mobileDisplay = getDeviceKindDependsOnViewPort() === DEVICES.MOBILE;

  return (
    <div className="AlertContainer container">
      <SvgIconLoader
        id={iconId}
        color={iconColor}
        kind={SVG_ICON_KIND.LIGHT}
        size={64}
      />
      {title && (
        <TextInfo
          className="AlertContainer__title"
          level={TEXT_HEADING_LEVEL.H2}
          kind={TEXT_KIND.HEADING}
          align={TEXT_ALIGN.LEFT}
          color={TEXT_COLOR.GREY6}
          size={TEXT_SIZE.TITLE4}
          fontType={TEXT_FONT_WEIGHT.REGULAR}
          text={title}
        />
      )}
      {msg && (
        <div className="AlertContainer__msg">
          {msg.map((text, index) => {
            const keyId = `stopper-alert-msg-${index}-${text}`;
            return (
              <TextInfo
                key={keyId}
                kind={TEXT_KIND.SPAN}
                display={TEXT_DISPLAY.BLOCK}
                align={TEXT_ALIGN.LEFT}
                color={TEXT_COLOR.GREY5}
                size={TEXT_SIZE.TITLE6}
                fontType={TEXT_FONT_WEIGHT.REGULAR}
                text={text}
              />
            );
          })}
        </div>
      )}
      {secondaryMessage && (
        <div className="AlertContainer__secondaryMessage">
          {secondaryMessage.map((text, index) => {
            const keyId = `stopper-alert-secondary-msg-${index}-${text}`;
            return (
              <TextInfo
                key={keyId}
                kind={TEXT_KIND.SPAN}
                display={TEXT_DISPLAY.BLOCK}
                align={TEXT_ALIGN.LEFT}
                color={TEXT_COLOR.GREY6}
                size={TEXT_SIZE.TITLE6}
                fontType={TEXT_FONT_WEIGHT.REGULAR}
                text={text}
              />
            );
          })}
        </div>
      )}
      {phone && (
        <div className="AlertContainer__phoneButton">
          <Button
            color={BUTTON_COLOR.BLUE_REVERSE}
            buttonType="button"
            link={`tel:${phone}`}
            forceExternal={true}
            label={phone}
            iconPosition={
              mobileDisplay ? ICON_POSITION.LEFT : ICON_POSITION.RIGHT
            }
            svgComponent={
              <SvgIconLoader
                id={ICONS_ID.Fijo}
                color={ICON_COLOR.GREEN}
                kind={SVG_ICON_KIND.SOLID}
                size={16}
              />
            }
            taggingInfo={{
              ...taggingInfo,
              componentContent: CALL_4_SELLING_CONTENT.T900,
            }}
            size={{
              w: BUTTON_WIDTH.FULL,
              h: BUTTON_HEIGHT.MEDIUM,
            }}
            desktopProps={{
              size: {
                w: BUTTON_WIDTH.MEDIUM,
                h: BUTTON_HEIGHT.REGULAR,
              },
            }}
          />
        </div>
      )}
      {link && link.label && link.url (
        <div className="AlertContainer__linkContainer">
          <CustomLink
            link={link.url}
            taggingInfo={{ ...taggingInfo, componentContent: link.label }}
          >
            <TextInfo
              kind={TEXT_KIND.SPAN}
              display={TEXT_DISPLAY.BLOCK}
              align={TEXT_ALIGN.CENTER}
              color={TEXT_COLOR.BLUE}
              size={TEXT_SIZE.CAPTION2}
              fontType={TEXT_FONT_WEIGHT.REGULAR}
              text={link.label}
            />
          </CustomLink>
        </div>
      )}
      {buttons && (
        <div className="AlertContainer__buttonsContainer">
          {buttons.map((btn, index) => {
            const keyId = `stopper-alert-btn-${index}-${btn.label}`;
            return (
              <Button
                color={btn.color}
                buttonType="button"
                onClick={btn.onClick}
                link={btn.link}
                label={btn.label}
                forceExternal={btn.forceExternal}
                key={keyId}
                taggingInfo={{
                  ...taggingInfo,
                  componentContent: btn.label,
                }}
                size={{
                  w: btn.size ? btn.size.w : BUTTON_WIDTH.FULL,
                  h: btn.size ? btn.size.h : BUTTON_HEIGHT.LARGE,
                }}
                desktopProps={{
                  size: {
                    w: btn?.desktopProps?.size?.w || BUTTON_WIDTH.MEDIUM,
                    h: btn?.desktopProps?.size?.h || BUTTON_HEIGHT.REGULAR,
                  },
                  hide: btn?.desktopProps?.hide || false
                }}
                tabletProps={{
                  size: {
                    w: btn?.tabletProps?.size?.w || BUTTON_WIDTH.MEDIUM,
                    h: btn?.tabletProps?.size?.h || BUTTON_HEIGHT.REGULAR,
                  },
                  hide: btn?.tabletProps?.hide || false
                }}
                className="AlertContainer__buttonsContainer--button"
              />
            );
          })}
        </div>
      )}
    </div>
  );
}

Alert.propTypes = {
  errorToPrint: PropTypes.shape({
    link: PropTypes.shape({
      label: PropTypes.string,
      url: PropTypes.string,
    }),
    title: PropTypes.string,
    msg: PropTypes.arrayOf(PropTypes.string),
    phone: PropTypes.string,
  }).isRequired,
};
